import type { RenderedViewLine } from '../../types/financialStatement';
import { CommentBadge } from './CommentBadge';

interface Props {
    line: RenderedViewLine;
    onLineClick: (lineNr: number, displayName: string) => void;
    onCommentClick: (lineNr: number, displayName: string) => void;
}

function formatAmount(amount: number): string {
    return new Intl.NumberFormat('de-CH', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(amount);
}

function formatPct(pct: number | null): string {
    if (pct === null) return '-';
    return `${pct > 0 ? '+' : ''}${pct.toFixed(1)}%`;
}

export function ViewLineRow({ line, onLineClick, onCommentClick }: Props) {
    const { RowType, DisplayName, IndentLevel, IsBold, SourceLineNr } = line;

    // Spacer row
    if (RowType === 'Spacer') {
        return <tr><td colSpan={7} className="py-3"></td></tr>;
    }

    // Header row
    if (RowType === 'Header') {
        return (
            <tr className="bg-gray-200">
                <td colSpan={7} className="px-3 py-2 text-sm font-bold text-gray-700 uppercase tracking-wider">
                    {DisplayName}
                </td>
            </tr>
        );
    }

    const isClickable = RowType === 'Data' && SourceLineNr;
    const isBold = IsBold === 1 || RowType === 'Subtotal' || RowType === 'Difference';
    const isSubtotalLike = RowType === 'Subtotal' || RowType === 'Difference';
    const paddingLeft = `${0.75 + IndentLevel * 1.5}rem`;

    return (
        <tr
            onClick={() => isClickable && onLineClick(SourceLineNr!, DisplayName)}
            className={`${
                isSubtotalLike
                    ? 'bg-gray-50 font-semibold border-t border-gray-300'
                    : isClickable ? 'hover:bg-blue-50 cursor-pointer' : ''
            } ${RowType === 'Data' && line.CurrentYearAmount === 0 && line.ComparisonYearAmount === 0 ? 'text-gray-400' : ''}`}
        >
            {/* Line number */}
            <td className="px-3 py-1.5 text-sm text-gray-500 whitespace-nowrap">
                {RowType === 'Data' ? SourceLineNr : ''}
            </td>

            {/* Display name */}
            <td
                className={`py-1.5 text-sm ${isBold ? 'font-semibold' : ''}`}
                style={{ paddingLeft }}
            >
                {DisplayName}
            </td>

            {/* Current year */}
            <td className="px-3 py-1.5 text-sm text-right tabular-nums whitespace-nowrap">
                {formatAmount(line.CurrentYearAmount)}
            </td>

            {/* Comparison year */}
            <td className="px-3 py-1.5 text-sm text-right tabular-nums whitespace-nowrap text-gray-500">
                {formatAmount(line.ComparisonYearAmount)}
            </td>

            {/* Variance CHF */}
            <td className={`px-3 py-1.5 text-sm text-right tabular-nums whitespace-nowrap ${
                line.VarianceCHF > 0 ? 'text-green-600' : line.VarianceCHF < 0 ? 'text-red-600' : ''
            }`}>
                {formatAmount(line.VarianceCHF)}
            </td>

            {/* Variance % */}
            <td className={`px-3 py-1.5 text-sm text-right tabular-nums whitespace-nowrap ${
                line.VariancePct !== null && line.VariancePct > 0 ? 'text-green-600' :
                line.VariancePct !== null && line.VariancePct < 0 ? 'text-red-600' : 'text-gray-400'
            }`}>
                {formatPct(line.VariancePct)}
            </td>

            {/* Comments */}
            <td className="px-2 py-1.5 text-center">
                {RowType === 'Data' && SourceLineNr && (
                    <CommentBadge
                        count={line.CommentCount}
                        onClick={() => onCommentClick(SourceLineNr, DisplayName)}
                    />
                )}
            </td>
        </tr>
    );
}
